import { defineStore } from "pinia";
import type { IJournal, IResearchTopic } from "~/types";

import { type IColor } from "@frontiers-vue3/public-pages";
import type { IContentfulJournal } from "~/types/contentful/IContentfulJournal";
import { useJournalPagesService } from "~/composables/useJournalPagesService";
import { useMongoDbService } from "~/composables/useMongoDbService";
import type { IAppMainStoreState } from "~/types/store";
import { buildClib3Breadcrumbs } from "~/helpers/buildClib3Breadcrumbs";
import { TopicStageEnum } from "~/enums";

export const useMain = defineStore("main", {
  state: (): IAppMainStoreState => {
    return {
      currentResearchTopic: {} as IResearchTopic,
      primaryJournal: {} as IContentfulJournal,
      totalArticles: 0,
      totalAuthors: 0,
      rtSponsorship: undefined,
      breadCrumbItems: []
    };
  },
  getters: {
    getPrimaryJournal(): IJournal | undefined {
      const primaryJournal = this.currentResearchTopic.journals?.find(
        (j) => j.isPrimary
      );
      if (primaryJournal) {
        return primaryJournal;
      } else if (
        !primaryJournal &&
        this.currentResearchTopic.journals.length > 0
      ) {
        return this.currentResearchTopic.journals[0];
      }
    },
    getPrimaryJournalId(): number | undefined {
      const primaryJournal = this.getPrimaryJournal;
      return primaryJournal?.id;
    },
    colorPalette(): IColor {
      return (this.primaryJournal?.palette as IColor) || "blue";
    },
    getParticipatingJournalsToSubmit(): IJournal[] {
      return this.currentResearchTopic.journals
        .filter((j) => j.isOpenForSubmission)
        .map((j) => {
          return {
            ...j,
            sections: j.sections?.filter((s) => s.isOpenForSubmission)
          };
        })
        .filter(
          (j) => j.taxonomy.find((t) => t.specialityId) || j.sections?.length
        );
    },
    getIsSubmitManuscriptEnabled(): boolean {
      const { isManuscriptButtonVisible } =
        useEnvironmentConfigStore().getConfig();
      return (
        this.getParticipatingJournalsToSubmit.length > 0 &&
        this.currentResearchTopic.isOpenForManuscriptSubmission &&
        !this.currentResearchTopic.closedOn &&
        !!isManuscriptButtonVisible
      );
    },
    isCurrentResearchTopicOpen(): boolean {
      return (
        this.currentResearchTopic.stageId === TopicStageEnum.Open &&
        this.currentResearchTopic.isOpenForSubmission
      );
    }
  },
  actions: {
    async fetchCurrentResearchTopic(researchTopicId: number) {
      const { getTerm } = useTerminologyStore();
      const { getResearchTopicDetails, getResearchTopicSponsorshipDetails } =
        useMongoDbService();

      const [rtDetails, rtSponsor] = await Promise.all([
        getResearchTopicDetails(researchTopicId, getTerm("tenantName")),
        getResearchTopicSponsorshipDetails(researchTopicId)
      ]);

      this.currentResearchTopic = rtDetails;
      this.rtSponsorship = rtSponsor;
    },
    fetchBreadcrumbs(rtDetails: IResearchTopic) {
      const router = useRequestURL();
      const runtimeConfig = useRuntimeConfig();

      this.breadCrumbItems = buildClib3Breadcrumbs(
        rtDetails,
        `${useGetBaseUrl(router, runtimeConfig)}`
      );
    },

    async fetchContentfulJournal(journalSlug: string) {
      const { getContentfulJournal } = useJournalPagesService();
      try {
        this.primaryJournal = await getContentfulJournal(journalSlug);
      } catch (err: unknown) {
        throw Error(
          `Error getting journal info ${journalSlug}. Message: ${
            err instanceof Error ? err.message : err
          }`
        );
      }
    },

    async fetchTotals() {
      const researchTopicId = this.currentResearchTopic.id;
      const { getTotalArticles, getTotalAuthors } = useRtApiService();

      const [totalArticles, totalAuthors] = await Promise.all([
        getTotalArticles(researchTopicId),
        getTotalAuthors(researchTopicId)
      ]);

      this.totalArticles = totalArticles;
      this.totalAuthors = totalAuthors;
    }
  }
});
